import * as ents from '@attain-apps/entities'
import { TypeChecker } from '@attain-apps/type-guards'
import type * as gridstack from 'gridstack'

import type * as state from '../../../../../../../state'

// Maximum number of attempts to optimize the grid
const MAX_ATTEMPTS = 5

/**
 * Attempts to optimize the layout of the current grid by removing invalid
 * widget frames and adjusting the size of existing widgets to fill empty space.
 * The process is repeated until the layout is considered valid or a maximum
 * number of attempts is reached.
 */
export function optimizeLayout(
  grid: gridstack.GridStack,
  widgetLookup: state.mosaic.dev.pages.models.widget.Frame.Lookup,
): void {
  if (!isLayoutValid(grid, widgetLookup)) {
    let attempts = 0

    // Continue looping until the layout is valid or the maximum number of
    // attempts is reached. If the layout remains invalid after the maximum
    // attempts, stop to prevent an infinite loop.
    while (attempts < MAX_ATTEMPTS) {
      // Start a batch update for performance
      grid.batchUpdate()

      // Perform the optimization steps
      _removeEmptyWidgetFrames(grid, widgetLookup)
      _stretchWidgetFramesHorizontally(grid)
      _stretchWidgetFramesVertically(grid)

      // Finalize batch update
      grid.commit()
      // Rearrange items to fill possible gaps
      grid.compact()

      // Exit the loop if the layout is valid
      if (isLayoutValid(grid, widgetLookup)) {
        break
      }

      attempts++
    }

    if (attempts === MAX_ATTEMPTS) {
      // TODO Add some warning to a user that layout is not optimized
      console.warn('Layout could not be optimized after 5 attempts.')
    }
  }
}

/**
 * Checks if the current grid layout is valid. A layout is considered valid if
 * there are no empty spaces and all widget frames are filled.
 */
export function isLayoutValid(
  grid: gridstack.GridStack,
  widgetLookup: state.mosaic.dev.pages.models.widget.Frame.Lookup,
): boolean {
  return !_hasEmptySpaces(grid) && !_hasEmptyWidgetFrames(grid, widgetLookup)
}

/**
 * Checks for any empty grid cells within the layout.
 * Iterates through each cell of the grid and looks for unoccupied spots.
 */
function _hasEmptySpaces(grid: gridstack.GridStack): boolean {
  const numberOfColumns = grid.getColumn()
  const numberOfRows = grid.getRow()

  // Iterate through the grid cells
  for (let y = 0; y < numberOfRows; y++) {
    for (let x = 0; x < numberOfColumns; x++) {
      // Check if the area (1x1 grid cell) is empty
      if (grid.isAreaEmpty(x, y, 1, 1)) {
        // Found an unfilled cell
        return true
      }
    }
  }

  return false
}

/**
 * Checks if there are any empty widget frame in the grid. An empty widget frame
 * is one that does not have a valid `gs-id` or is not found in the widget
 * lookup from the state.
 */
function _hasEmptyWidgetFrames(
  grid: gridstack.GridStack,
  widgetLookup: state.mosaic.dev.pages.models.widget.Frame.Lookup,
): boolean {
  // Check if there are any empty widget frames in the grid
  return grid.getGridItems().some(_widgetFrame => {
    const widgetFrameId = _widgetFrame.getAttribute('gs-id')
    return !widgetFrameId || !widgetLookup[widgetFrameId]
  })
}

/**
 * Clears any empty {@link state.mosaic.dev.pages.models.Frame} in the {@link State.grid} that are not associated
 * with matching {@link ents.core.mosaic.Widget.Rendering}
 *
 */
function _removeEmptyWidgetFrames(
  grid: gridstack.GridStack,
  widgetLookup: state.mosaic.dev.pages.models.widget.Frame.Lookup,
): void {
  for (const item of grid.getGridItems()) {
    const widgetId = item.getAttribute('gs-id')
    // Remove widget if no valid `gs-id` or it's not found in the widget lookup store
    if (!widgetId || !widgetLookup[widgetId]) {
      grid.removeWidget(item)
    }
  }
}

/**
 * Expands widget frames horizontally to occupy available empty space
 * on their left and right sides within the same row.
 */
function _stretchWidgetFramesHorizontally(grid: gridstack.GridStack): void {
  const numberOfColumns = grid.getColumn()

  grid
    .getGridItems()
    .map(({ gridstackNode }) => gridstackNode)
    // Skip invalid items (that doesn't have x or y coordinates or if HTML
    // element is not defined)
    .filter(
      (
        node,
      ): node is gridstack.GridStackNode & {
        el: gridstack.GridItemHTMLElement
      } =>
        TypeChecker.isNotNullish(node?.y) &&
        TypeChecker.isNotNullish(node.x) &&
        TypeChecker.isNotNullish(node.el),
    )
    // Sort by row position
    .sort((a, b) => (a.y ?? 0) - (b.y ?? 0))
    .forEach(item => {
      // We know column and row will be defined because of the filter above but
      // TypeScript is complaining that it can be undefined so we need to add
      // default values
      const {
        el: element,
        x: columnPosition = 0,
        y: rowPosition = 0,
        // NOTE: Height and width may be undefined, as they are only set when
        // greater than 1. Since the library defines these values only in that
        // case, we need to set a default of 1.
        h: height = 1,
        w: width = 1,
      } = item

      // Track how much item will expand to the left if there are empty spaces
      // next to item
      let leftExpand = 0
      // Track how much item will expand to the right if there are empty
      // spaces next to item
      let rightExpand = 0

      // Check left expansion
      for (let leftColumn = columnPosition - 1; leftColumn >= 0; leftColumn--) {
        // Check if previous cell is empty
        if (grid.isAreaEmpty(leftColumn, rowPosition, 1, height)) {
          leftExpand++
        } else {
          break
        }
      }

      // Check right expansion
      for (
        let rightColumn = columnPosition + width;
        rightColumn < numberOfColumns;
        rightColumn++
      ) {
        // Check if next cell is empty
        if (grid.isAreaEmpty(rightColumn, rowPosition, 1, height)) {
          rightExpand++
        } else {
          break
        }
      }

      if (leftExpand === 0 && rightExpand === 0) {
        // No expansion needed
        return
      }

      // Summarize new width of the item
      const newWidth = width + leftExpand + rightExpand
      // Adjust the column position if item needs to expand to the left
      const newColumnPosition = columnPosition - leftExpand

      // Update the item in the grid
      grid.update(element, { w: newWidth, x: newColumnPosition })
    })
}

/**
 * Expands widget frames vertically to occupy available empty space below the
 * items.
 */
function _stretchWidgetFramesVertically(grid: gridstack.GridStack): void {
  const numberOfRows = grid.getRow()

  grid
    .getGridItems()
    .map(({ gridstackNode }) => gridstackNode)
    // Skip invalid items (that doesn't have x or y coordinates or if HTML
    // element is not defined)
    .filter(
      (
        node,
      ): node is gridstack.GridStackNode & {
        el: gridstack.GridItemHTMLElement
      } =>
        TypeChecker.isNotNullish(node?.y) &&
        TypeChecker.isNotNullish(node.x) &&
        TypeChecker.isNotNullish(node.el),
    )
    // Sort by row position
    .sort((a, b) => (a.y ?? 0) - (b.y ?? 0))
    .forEach(item => {
      // We know column and row will be defined because of the filter above but
      // TypeScript is complaining that it can be undefined so we need to add
      // default values
      const {
        el: element,
        x: columnPosition = 0,
        y: rowPosition = 0,
        // NOTE: Height and width may be undefined, as they are only set when
        // greater than 1. Since the library defines these values only in that
        // case, we need to set a default of 1.
        h: height = 1,
        w: width = 1,
      } = item
      // Track how much item will expand to the bottom if there are empty spaces
      // below the item
      let bottomExpand = 0

      // Check bottom expansion
      for (
        let bottomColumn = rowPosition + height;
        bottomColumn < numberOfRows;
        bottomColumn++
      ) {
        // Check if cell below is empty
        if (grid.isAreaEmpty(columnPosition, bottomColumn, width, 1)) {
          bottomExpand++
        } else {
          break
        }
      }

      // Summarize new height of the item
      const newHeight = height + bottomExpand

      // Update the item in the grid
      grid.update(element, { h: newHeight })
    })
}
